import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePickerComponent, LoadingButtonComponent } from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { CreateSaleRequest, ProblemDetails, QuoteInfo } from '../../sdk/sdk';
import { DateService } from '../../services/date.service';
import { ModalService } from '../../services/modal.service';
import { SalesService } from '../../services/sales.service';

function readProblemDetails(error: unknown): ProblemDetails | null {
  if (!error || typeof error !== 'object') return null;
  const e = error as { detail?: unknown; response?: unknown };
  if (typeof e.detail === 'string' && e.detail.length > 0) {
    return e as ProblemDetails;
  }
  if (typeof e.response === 'string' && e.response.length > 0) {
    try {
      const parsed = JSON.parse(e.response);
      if (parsed && typeof parsed === 'object') {
        return parsed as ProblemDetails;
      }
    } catch {
      return null;
    }
  }
  return null;
}

@Component({
  selector: 'app-confirm-sale-modal',
  imports: [CommonModule, ReactiveFormsModule, DatePickerComponent, LoadingButtonComponent],
  templateUrl: './confirm-sale-modal.component.html',
})
export class ConfirmSaleModalComponent implements OnInit {
  readonly activeModal = inject(NgbActiveModal);
  private readonly salesService = inject(SalesService);
  private readonly modalService = inject(ModalService);
  private readonly dateService = inject(DateService);

  opportunityId!: string;
  quote!: QuoteInfo;
  showDatePicker = false;
  minDate?: Date;
  maxDate?: Date;
  isSubmitting = false;
  isDateDisabled = false;
  readonly markDisabled = (date: Date | NgbDateStruct): boolean =>
    this.dateService.isWeekend(this.dateService.toDate(date));

  confirmForm = new FormGroup({
    dateSold: new FormControl<Date>(new Date(), Validators.required),
  });

  ngOnInit(): void {
    if (this.isAutoApprovedSale()) {
      this.initializeAutoApprovedSale();
    } else {
      this.initializeManualApproval();
    }
  }

  private isAutoApprovedSale(): boolean {
    return !!this.quote.sale;
  }

  private initializeAutoApprovedSale(): void {
    const soldAt = this.quote.sale?.soldAt;

    if (soldAt) {
      const soldDate = new Date(soldAt);
      soldDate.setHours(0, 0, 0, 0);
      this.confirmForm.patchValue({ dateSold: soldDate });
    }

    this.isDateDisabled = true;
    this.confirmForm.get('dateSold')?.disable();
  }

  private initializeManualApproval(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.confirmForm.patchValue({ dateSold: today });

    this.isDateDisabled = true;
    this.confirmForm.get('dateSold')?.disable();
  }

  hasError(controlName: string): boolean {
    const control = this.confirmForm.get(controlName);
    if (!control) return false;
    return control.invalid && control.touched;
  }

  toggleDatePicker(): void {
    if (!this.isDateDisabled) {
      this.showDatePicker = !this.showDatePicker;
    }
  }

  onDateChange(date: Date | null): void {
    if (date) {
      this.confirmForm.patchValue({ dateSold: date });
      this.showDatePicker = false;
    }
  }

  onSubmit(): void {
    if (this.isSubmitting) {
      return;
    }

    const formValue = this.confirmForm.getRawValue();
    if (formValue.dateSold) {
      this.approveSale();
    } else {
      this.confirmForm.markAllAsTouched();
    }
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  private approveSale(): void {
    const dateSold = this.confirmForm.getRawValue().dateSold;
    if (!dateSold) {
      console.error('ConfirmSaleModalComponent: Date sold is invalid');
      return;
    }

    this.isSubmitting = true;

    const request = new CreateSaleRequest({
      opportunityId: this.opportunityId,
      quoteId: this.quote.id ?? '',
      saleAmount: this.quote.saleAmount ?? 0,
      soldAt: dateSold,
    });

    this.salesService.createSale(request).subscribe({
      next: (sale) => {
        this.activeModal.close(sale);

        this.modalService.openSuccessModal(
          'Sale Approved',
          'The sale has been successfully approved.',
        );
      },
      error: (error) => {
        console.error('ConfirmSaleModalComponent: Error approving sale:', error);
        this.isSubmitting = false;

        const problem = readProblemDetails(error);
        let errorMessage: string;
        switch (error?.status) {
          case 400:
            errorMessage =
              problem?.detail ||
              'The request could not be processed. Please review the sale and try again.';
            break;
          case 403:
            errorMessage = 'The current logged in user is not allowed to perform this action';
            break;
          case 404:
            errorMessage = problem?.detail || 'The opportunity or quote could not be found';
            break;
          case 500:
            errorMessage = problem?.detail || 'Internal server error occurred';
            break;
          default:
            errorMessage =
              problem?.detail ||
              error?.message ||
              'An unexpected error occurred while approving the sale. Please try again.';
        }
        this.modalService.openErrorModal('Error Approving Sale', errorMessage);
      },
    });
  }
}
